import React from 'react';
import { Link } from 'react-router-dom';
import './Technology.css';

const facilities = [
  { icon: '🏭', title: '40,000 Sq Ft Facility', desc: 'State-of-the-art manufacturing floor in Bangalore with dedicated zones for SMT, THT, testing, box build, and logistics.' },
  { icon: '🔬', title: 'Class 100K Clean Room', desc: 'ISO 8 clean room for precision assembly of medical, optical, and high-reliability electronics requiring controlled environments.' },
  { icon: '⚡', title: '2 SMT Lines', desc: 'High-speed surface mount lines capable of 1.5M+ component placements per hour, handling 0201 to large BGAs.' },
  { icon: '🔩', title: '5 Through-Hole Lines', desc: 'Dedicated THT wave soldering and selective soldering lines for mixed-technology assemblies.' },
  { icon: '📊', title: 'SPC Monitoring', desc: 'Real-time Statistical Process Control monitoring across all production lines with automatic out-of-control alerts.' },
  { icon: '📦', title: 'Bonded Warehouse', desc: 'On-site bonded warehouse for customer-owned material, JIT delivery management, and kitting operations.' },
];

const equipment = [
  { category: 'SMT Assembly', items: ['High-Speed Chip Shooters (0201–50mm)', 'Flexible Placement for BGA/QFN/μBGA', 'Lead-Free & Leaded Reflow Ovens', 'Automated Solder Paste Printers with SPI'] },
  { category: 'Inspection & Testing', items: ['3D Automated Optical Inspection (AOI)', 'X-Ray Inspection (BGA & Hidden Joints)', 'In-Circuit Test (ICT) Fixtures', 'Functional Test Stations'] },
  { category: 'PCB Fabrication', items: ['CNC Drilling & Routing', 'Laser Direct Imaging (LDI)', 'AOI for Bare Board Inspection', 'Impedance Test Equipment'] },
  { category: 'Post Assembly', items: ['Conformal Coating Line', 'Selective Soldering', 'Burn-In Racks & ESS Chambers', 'Automated Box Build Assembly'] },
];

const qualitySteps = [
  { step: '01', title: 'Incoming QC', desc: 'Every component batch inspected and verified against approved vendor certificates before entering production.' },
  { step: '02', title: 'Paste Inspection (SPI)', desc: 'Solder paste deposition verified with 3D SPI after every print cycle — the #1 driver of defects, fully controlled.' },
  { step: '03', title: 'AOI After Placement', desc: 'Automated optical inspection after reflow catches component presence, polarity, and solder joint quality.' },
  { step: '04', title: 'X-Ray Inspection', desc: 'All BGA, QFN, and hidden-joint components verified with X-ray for voiding and bridging defects.' },
  { step: '05', title: 'Functional Testing', desc: 'Custom test fixtures power-on and verify every board against its functional specification before shipping.' },
  { step: '06', title: 'Traceability', desc: 'Every unit serialized with component batch traceability — full audit trail from raw material to finished product.' },
];

export default function Technology() {
  return (
    <main className="technology-page page-wrapper">
      <section className="page-hero">
        <div className="page-hero__bg" />
        <div className="container page-hero__inner">
          <div className="section-tag" style={{ background: 'rgba(0,194,224,0.15)', color: 'var(--cyan)' }}>Technology</div>
          <h1 className="page-hero__title">Infrastructure Built for <span>Precision at Scale</span></h1>
          <p className="page-hero__subtitle">
            Our 40,000 sq ft facility and advanced equipment stack are designed for one thing: consistent, traceable, high-quality electronics manufacturing.
          </p>
        </div>
      </section>

      {/* Facilities */}
      <section className="section">
        <div className="container">
          <div className="section-center">
            <div className="section-tag">Our Facility</div>
            <h2 className="section-title">World-Class <span>Infrastructure</span></h2>
          </div>
          <div className="facilities-grid">
            {facilities.map((f) => (
              <div key={f.title} className="facility-card">
                <div className="facility-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment */}
      <section className="section equipment-section">
        <div className="container">
          <div className="section-center">
            <div className="section-tag">Equipment</div>
            <h2 className="section-title">Built on <span>Best-in-Class Tools</span></h2>
          </div>
          <div className="equipment-grid">
            {equipment.map((cat) => (
              <div key={cat.category} className="equipment-card">
                <h3>{cat.category}</h3>
                <ul>
                  {cat.items.map(item => (
                    <li key={item}>
                      <span className="equip-dot" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Process */}
      <section className="section quality-section">
        <div className="container">
          <div className="section-center">
            <div className="section-tag">Quality System</div>
            <h2 className="section-title">Quality Built Into <span>Every Step</span></h2>
            <p className="section-subtitle" style={{ margin: '0 auto 56px' }}>
              We don't inspect quality in — we build it in. Here's how our quality gates work on every production run.
            </p>
          </div>
          <div className="quality-steps">
            {qualitySteps.map((q, i) => (
              <div key={q.step} className="quality-step">
                <div className="quality-step-num">{q.step}</div>
                <div className="quality-step-content">
                  <h3>{q.title}</h3>
                  <p>{q.desc}</p>
                </div>
                {i < qualitySteps.length - 1 && <div className="quality-arrow">→</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section tech-stats-section">
        <div className="container">
          <div className="tech-stats-grid">
            {[
              { n: '1.5M+/hr', l: 'Component Placement Rate' },
              { n: '98.7%', l: 'Average First-Pass Yield' },
              { n: '100%', l: 'AOI Coverage' },
              { n: '100%', l: 'Unit Traceability' },
              { n: '48hr', l: 'Prototype Turnaround' },
              { n: '6+', l: 'Years Operating' },
            ].map(s => (
              <div key={s.l} className="tech-stat">
                <strong>{s.n}</strong>
                <span>{s.l}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-banner">
        <div className="container cta-banner__inner">
          <div className="cta-banner__text">
            <h2>Ready to See Our <span>Facility?</span></h2>
            <p>We welcome factory visits and audits. Contact us to schedule a tour of our Bangalore manufacturing facility.</p>
          </div>
          <div className="cta-banner__actions">
            <Link to="/contact" className="btn-primary">Schedule a Factory Visit →</Link>
            <Link to="/services" className="btn-outline-white">Our Services</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
